import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import {
	formatIndex,
	formatNumber,
	localizeDigits,
} from '@sohanscript/lib/utils/format';
import { Pendulamp } from '@sohanscript/ui/pendulamp';
import { useLocation } from '@tanstack/react-router';
import type * as React from 'react';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import { Terminal, TerminalProgressBar } from '@/components/ui/terminal';
import { services } from '@/lib/data/services';
import {
	categoryLabels,
	localizeServiceBySlug,
	localizeServices,
} from '@/lib/data/services-i18n';
import { CheckTags } from './check-tags';
import { TelemetryRows } from './telemetry-rows';

type ServiceDetailsProps = React.ComponentProps<'div'> & { slug: string };

function SectionHeader({ path, note }: { path: string; note?: string }) {
	return (
		<div className="mb-8 flex flex-wrap items-baseline justify-between gap-2 border-border/50 border-b pb-3">
			<h2 className="font-mono font-semibold text-primary text-sm uppercase tracking-widest">
				{path}
			</h2>
			{note && (
				<span className="text-2xs text-muted-foreground uppercase tracking-widest">
					{note}
				</span>
			)}
		</div>
	);
}

function TelemetryCard({
	params,
}: {
	params: { key: string; value: string }[];
}) {
	return (
		<Terminal
			label="~/telemetry/spec"
			sequence={false}
			className="h-full max-w-full"
		>
			{/* Status chip — distinct from data rows, body font (not mono costume) */}
			<span
				role="status"
				className="flex w-fit items-center gap-2 rounded-full border border-success/30 bg-success/10 px-3 py-0.5 font-sans text-success text-xs uppercase tracking-wider"
			>
				<span className="size-1.5 animate-pulse rounded-full bg-success" />
				{m.service_status_running()}
			</span>

			{/* Config params — mono data rows with rhythmic separators */}
			<TelemetryRows params={params} className="mt-3" />

			{/* NODE HEALTH — single source of health status (redundant nodes: line removed) */}
			<div className="mt-4 whitespace-normal border-border/40 border-t pt-3">
				<TerminalProgressBar label={m.service_node_health()} duration={1.6} />
			</div>
		</Terminal>
	);
}

export function ServiceDetails({
	className,
	slug,
	...props
}: ServiceDetailsProps) {
	const service = localizeServiceBySlug(slug);
	const location = useLocation();

	if (!service) return null;

	const adjacent = localizeServices().filter((s) => s.slug !== slug);

	return (
		<div className={cn('flex flex-col gap-16', className)} {...props}>
			<section className="grid gap-10 lg:grid-cols-[1fr_380px] lg:items-stretch">
				<div
					style={{ viewTransitionName: `vt-service-${service.slug}` }}
					className="flex flex-col justify-center gap-6"
				>
					<div className="flex flex-wrap items-center gap-2 text-2xs uppercase tracking-widest">
						<span className="flex size-10 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-primary">
							<Iconify
								icon={service.icon}
								className="size-5"
								aria-hidden="true"
							/>
						</span>
						<span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-0.5 text-primary">
							{service.categoryLabel}
						</span>
						<span className="text-muted-foreground">
							{categoryLabels[service.category]()}
						</span>
						{service.flagship && (
							<span className="rounded-full border border-success/30 bg-success/10 px-3 py-0.5 text-success">
								{m.svc_flagship()}
							</span>
						)}
					</div>

					<h1 className="font-xblk text-3xl text-primary uppercase leading-[0.9] tracking-[0.01em] md:text-5xl">
						{service.title}
					</h1>

					<p className="max-w-xl text-foreground/75 leading-relaxed lg:text-lg">
						{service.tagline}
					</p>

					<p className="font-mono text-2xs text-muted-foreground uppercase tracking-widest">
						{m.svd_model()}
					</p>

					<div className="mt-2 flex flex-wrap items-center gap-3">
						<Button href={`/start?service=${service.slug}`}>
							{m.cta_start()}
							<Iconify
								icon="lucide:arrow-up-right"
								className="size-4 rtl:-scale-x-100"
							/>
						</Button>
						<Button href={`${location.pathname}#~_process`} variant="opacity">
							<Iconify
								icon="dinkie-icons:heavy-dollar-sign"
								className="size-4"
							/>{' '}
							read --execution-process
						</Button>
					</div>
				</div>

				<TelemetryCard params={service.telemetry} />
			</section>

			<section id="~_scope" className="scroll-mt-(--height-navbar)">
				<SectionHeader
					path="~/scope"
					note={m.svd_modules({
						count: formatNumber(service.scope.length),
					})}
				/>
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{service.scope.map((item, index) => (
						<article
							key={item.id}
							className="corner-bevel glow-hover flex flex-col rounded-xl border border-primary/20 bg-background-light p-5"
						>
							<div className="flex items-center justify-between gap-2">
								<span className="font-mono text-primary/50 text-xs tabular-nums">
									{formatIndex(index + 1)}
								</span>
								<span className="text-2xs text-muted-foreground uppercase tracking-widest">
									{item.categoryTag}
								</span>
							</div>
							<div className="mt-2 flex items-center justify-between gap-2">
								<h3 className="font-medium text-primary">{item.title}</h3>
								<Iconify
									icon="lucide:arrow-right"
									className="size-4 shrink-0 text-primary/50 rtl:-scale-x-100"
								/>
							</div>
							<p className="mt-2 text-foreground/75 text-sm leading-relaxed">
								{item.description}
							</p>
							<CheckTags tags={item.tags} className="mt-4 gap-y-1.5" />
						</article>
					))}
				</div>
			</section>

			<section>
				<SectionHeader path="~/stack" note={m.svd_runtimes_note()} />
				<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
					{service.stackGroups.map((group) => (
						<article
							key={group.group}
							className="corner-bevel rounded-xl border border-primary/20 bg-background-light p-5"
						>
							<h3 className="text-2xs text-primary uppercase tracking-widest">
								{group.group}
							</h3>
							<div className="mt-3 flex flex-wrap gap-1.5">
								{group.items.map((tech) => (
									<span
										key={tech}
										className="rounded-md border border-border/60 px-2 py-0.5 text-2xs text-foreground/80"
									>
										{tech}
									</span>
								))}
							</div>
						</article>
					))}
				</div>
			</section>

			<section id="~_process" className="scroll-mt-(--height-navbar)">
				<SectionHeader
					path="~/process"
					note={m.svd_pipeline_note({
						count: formatNumber(service.process.length),
					})}
				/>
				<ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
					{service.process.map((stage, index) => (
						<li
							key={stage.step}
							className="corner-bevel glow-hover flex flex-col rounded-xl border border-primary/20 bg-background-light p-5"
						>
							<span className="font-mono text-primary/50 text-xs tabular-nums">
								{formatIndex(index + 1)}
							</span>
							<span className="mt-2 text-primary text-sm uppercase tracking-widest">
								{stage.step}
							</span>
							<p className="mt-2 text-foreground/75 text-sm leading-relaxed">
								{stage.detail}
							</p>
							<span className="mt-auto flex items-start gap-1.5 pt-4 text-2xs text-muted-foreground uppercase tracking-widest">
								<span className="shrink-0 text-success">{m.svd_output()}</span>
								<span>{stage.output}</span>
							</span>
						</li>
					))}
				</ol>
			</section>

			<section id="~_work-case-study" className="scroll-mt-(--height-navbar)">
				<SectionHeader
					path="~/work-case-study"
					note={m.svd_work_case_study_note()}
				/>
				<div className="grid gap-4 lg:grid-cols-[1fr_280px]">
					{service.relatedWork ? (
						<div className="grid gap-4">
							{service.relatedWork.map((work) => (
								<Link
									key={work.slug}
									to="/work/$slug"
									params={{ slug: work.slug } as never}
									className="group corner-bevel glow-hover flex flex-col rounded-xl border border-primary/20 bg-background-light p-6"
								>
									<div className="flex flex-wrap items-center justify-between gap-2">
										<span className="font-mono text-2xs text-muted-foreground uppercase tracking-widest">
											~/work/{work.slug}
										</span>
										<span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-0.5 text-2xs text-primary uppercase tracking-widest">
											{work.badge}
										</span>
									</div>

									<h3 className="mt-3 flex items-center gap-2 font-xblk text-primary text-xl uppercase leading-[0.9] lg:text-2xl">
										{work.title}
										<Iconify
											icon="lucide:arrow-up-right"
											className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5"
										/>
									</h3>

									<p className="mt-2 max-w-prose text-foreground/75 text-sm leading-relaxed">
										{work.description}
									</p>

									<div className="mt-5 grid grid-cols-3 gap-3 border-border/40 border-y py-4">
										{work.metrics.map((metric) => (
											<div key={metric.label} className="flex flex-col gap-0.5">
												<span className="font-mono text-lg text-primary tabular-nums">
													{localizeDigits(metric.value)}
												</span>
												<span className="text-2xs text-muted-foreground uppercase tracking-widest">
													{metric.label}
												</span>
											</div>
										))}
									</div>

									<div className="mt-4 flex flex-wrap gap-1.5">
										{work.tech.map((tech) => (
											<span
												key={tech}
												className="rounded-md border border-border/60 px-2 py-0.5 text-2xs text-foreground/80"
											>
												{tech}
											</span>
										))}
									</div>
								</Link>
							))}
						</div>
					) : (
						<div className="corner-bevel relative flex flex-col rounded-xl border border-border/50 bg-background-light p-6">
							<Pendulamp />
							<span className="text-2xs text-muted-foreground uppercase tracking-widest">
								{m.svd_case_study()}
							</span>
							<p className="mt-2 max-w-prose text-foreground/75 text-sm leading-relaxed">
								{m.svd_case_study_desc()}
							</p>
							<Button variant="outline" href="/start" className="mt-4 w-fit">
								{m.svd_request_cases()}
							</Button>
						</div>
					)}

					<aside className="flex flex-col gap-3">
						<h3 className="text-2xs text-muted-foreground uppercase tracking-widest">
							{m.svd_adjacent()}
						</h3>
						{adjacent.slice(0, 4).map((s) => (
							<Link
								key={s.slug}
								to="/build/$slug"
								params={{ slug: s.slug } as never}
								className="group corner-bevel glow-hover flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-background-light px-4 py-3"
							>
								<span className="flex min-w-0 items-center gap-3">
									<span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/5 text-primary">
										<Iconify
											icon={s.icon}
											className="size-4"
											aria-hidden="true"
										/>
									</span>
									<span className="min-w-0">
										<span className="block truncate font-medium text-primary text-sm">
											{s.title}
										</span>
										<span className="font-mono text-2xs text-muted-foreground uppercase tracking-widest">
											~/build/{s.slug}
										</span>
									</span>
								</span>
								<Iconify
									icon="lucide:arrow-right"
									className="size-4 shrink-0 text-primary/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5"
								/>
							</Link>
						))}
						<Link
							to="/services"
							className="group corner-bevel -mt-5 flex items-center justify-between gap-3 rounded-lg rounded-t-none border border-t-0 px-2 pt-4 pb-2 text-2xs text-muted-foreground uppercase tracking-widest transition-colors hover:text-primary"
						>
							{m.svd_view_all({
								count: formatNumber(services.length),
							})}
							<Iconify
								icon="lucide:layout-grid"
								className="size-3.5 transition-transform group-hover:translate-x-0.5"
							/>
						</Link>
					</aside>
				</div>
			</section>
		</div>
	);
}
